import { Component, OnInit, ViewChild } from '@angular/core';
import { AuthService } from '../../shared/services/auth.service';
import { SidenavService } from '../../shared/services/sidenav.service';
import { LanguageService } from '../../shared/services/language.service';
import { UsersManagementService } from '../../shared/services/usersManagement.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { ManageUsersDialogComponent } from '../manage-users-dialog/manage-users-dialog.component';
import { ScrollControlService } from '../../shared/services/scroll-control.service';
import { Subject, throwError } from 'rxjs';
import { GenericDialogComponent } from '../../shared/generic-dialog/generic-dialog.component';
import { InfoBarWithStatusService } from '../../shared/services/info-bar-with-status.service';
import { getUserErrorMessages } from 'src/app/constants/user-error-messages';
import { retryWhen, catchError, scan, delay, filter, switchMap, take, debounceTime, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { Permission } from '../../shared/security/permission';
import { PermissionService } from '../../shared/services/permission.service';
import { TenantsService, TenantDto } from '../../shared/services/tenants.service';
import { tenantIdGuard } from '../../shared/services/tenant-id.guard';

@Component({
  standalone: false,
  selector: 'app-users-management',
  templateUrl: './users-management.component.html',
  styleUrls: ['./users-management.component.css']
})
export class UsersManagementComponent implements OnInit {
  variables: any;
  isOpen: boolean = false;
  shouldPinSidenav: boolean = false;
  dataSourceUsers = new MatTableDataSource<any>([]);
  colorWhite = '#fff';
  formGroup1: any;
  displayedColumns1: string[] = ['name', 'email', 'role', 'tenant', 'active', 'actions'];
  Permission = Permission;
  id: string = '';
  /** Logged-in user (from /me); used to fix tenant for non–super-admins. */
  currentUser: any;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  isLoading: boolean = true;
  errorDetected: boolean = false;
  errorText: any;

  paginatorStart: number = 0;
  searchSubject = new Subject<string>();
  searchText: string = "";

  errorMessages: any;

  paginatorPageSize: number = 10;
  paginatorTotalElements: number = 0;
  paginatorCurrentIndex: number = 0;
  filterDirection: string = '';
  /** Empty string = all tenants (super-admin only). */
  selectedTenantId: string = '';
  tenants: TenantDto[] = [];
  showTenantFilter: boolean = false;

  constructor(
    private authService: AuthService,
    private sidenavService: SidenavService,
    private languageService: LanguageService,
    public dialog: MatDialog,
    private infoBar: InfoBarWithStatusService,
    private router: Router,
    private _formBuilder: FormBuilder,
    private usersManagementService: UsersManagementService,
    private scrollService: ScrollControlService,
    private permissionService: PermissionService,
    private tenantsService: TenantsService
  ) { }

  ngOnInit(): void {
    this.scrollService.setScroll(true);

    this.languageService.translations$.subscribe((vars: any) => {
      this.variables = vars;
    });

    this.formGroup1 = this._formBuilder.group({
      regionsFilterCtrl: [''],
      regionCtrl: ['', Validators.required],
    });

    this.searchFilter('')

    this.authService.userDataLoaded$.pipe(
      filter(loaded => loaded),
      take(1),
      switchMap(() => this.authService.getUserData().pipe(take(1)))
    ).subscribe(user => {
      this.currentUser = user;
      this.id = user.id;
      this.showTenantFilter = this.canAssignUserCrossTenant();
      if (this.showTenantFilter) {
        this.tenantsService.list(false).subscribe({
          next: (tenants) => { this.tenants = tenants ?? []; },
          error: (err) => console.error('Failed to load tenants for filter', err)
        });
      }
    })

    this.searchSubject.pipe(debounceTime(1000)).subscribe((searchTerm) => {
      this.searchFilter(searchTerm)
    });

    // put the rest of the error messages here
    this.errorMessages = getUserErrorMessages(this.variables);

    this.usersManagementService.getAllUsers().pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, error) => {
            if (retryCount >= 3) {
              throw error;
            }
            return retryCount + 1;
          }, 0),
          delay(2500)
        )
      ),
      catchError(err => {
        console.error('Error:', err);

        this.errorDetected = true;
        const message = this.errorMessages[err.error.message]
        this.errorText = message ? message : this.variables.detectedError;

        return throwError(() => err);
      })
    ).subscribe({
      next: (data) => {
      },
      complete: () => this.isLoading = false
    });
  }

  onSidenavToggle() {
    this.isOpen = !this.isOpen;
    this.sidenavService.sideNavState$.next(this.isOpen);
  }



  searchFilter(searchTerm: string) {
    this.isLoading = true;
    this.paginatorCurrentIndex = 0;
    this.searchText = searchTerm;

    this.performUserSearch(this.buildSearchQuery(0));
  }

  private buildSearchQuery(page: number) {
    const query: { filter: string; page: number; size: number; tenantId?: string } = {
      filter: this.searchText,
      page,
      size: this.paginatorPageSize
    };
    if (this.showTenantFilter && this.selectedTenantId) {
      query.tenantId = this.selectedTenantId;
    }
    return query;
  }

  onTenantFilterChange(tenantId: string) {
    this.selectedTenantId = tenantId ?? '';
    this.searchFilter(this.searchText);
  }

  private performUserSearch(queryParams: { filter: string; page: number; size: number; tenantId?: string }) {
    this.usersManagementService.searchUsers(queryParams).pipe(
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, error) => {
            if (retryCount >= 3) {
              throw error;
            }
            return retryCount + 1;
          }, 0),
          delay(2500)
        )
      ),
      catchError(err => {
        console.error('Error:', err);

        this.errorDetected = true;
        const message = this.errorMessages[err.error.message]
        this.errorText = message ? message : this.variables.detectedError;

        return throwError(() => err);
      })
    ).subscribe({
      next: (data) => this.handleSearchResults(data),
      complete: () => this.isLoading = false
    });
  }

  private handleSearchResults(data: any) {
    if (data) {
      this.dataSourceUsers.data = data.users ?? [];
      this.paginatorTotalElements = data.totalUsers ?? 0;
      this.errorDetected = false;
      if (this.paginator) {
        this.paginator.length = this.paginatorTotalElements;
        this.paginator.pageIndex = this.paginatorCurrentIndex;
      }
    }
  }

  onCheckboxChange(event: any) {
    const filtro = event.target.value;
    this.searchSubject.next(filtro);
  }


  onPageChange(event: PageEvent) {
    if (this.paginatorCurrentIndex !== event.pageIndex) {
      this.paginatorCurrentIndex = event.pageIndex;
      this.isLoading = true;
      this.performUserSearch(this.buildSearchQuery(event.pageIndex));
    }
  }

  /**
   * Only super-admins may assign users to any organization; tenant admins (e.g. ADMIN) keep users in their tenant.
   */
  canAssignUserCrossTenant(): boolean {
    return this.permissionService.hasPermission(Permission.TENANT_MANAGE)
      && this.currentUser?.role === 'SUPER-ADMIN';
  }

  /** Avoid opening user dialogs before /me is loaded (permissions + role + tenant). */
  private runWhenCurrentUserReady(action: () => void): void {
    if (this.currentUser) {
      action();
      return;
    }
    this.authService.userDataLoaded$.pipe(
      filter(loaded => loaded),
      take(1),
      switchMap(() => this.authService.getUserData().pipe(take(1)))
    ).subscribe({
      next: (u) => {
        this.currentUser = u;
        if (u?.id) {
          this.id = u.id;
        }
        action();
      },
      error: () => {
        this.infoBar.showSnackbar(this.variables?.detectedError ?? 'Error', ['error-snackbar'], 4000);
      }
    });
  }

  addUser() {
    this.runWhenCurrentUserReady(() => {
      const canAssign = this.canAssignUserCrossTenant();
      const tid = (this.currentUser?.tenantId ?? '').trim();
      const data = {
        title: this.variables.addUser,
        element: { name: '', lastName: '', role: '', email: '' },
        canAssignUserTenant: canAssign,
        fixedTenantId: canAssign ? undefined : tid,
        fixedTenantName: canAssign ? undefined : ((this.currentUser?.tenantName ?? '').trim() || undefined)
      };

      const dialogRef = this.dialog.open(ManageUsersDialogComponent, { data });

      dialogRef.afterClosed().subscribe((result: any) => {
        if (!result) {
          return;
        }
        const user: any = {
          name: result.data.addNameCtrl,
          lastName: result.data.addLastNameCtrl,
          email: result.data.addEmailCtrl
        };
        if (result.data.tenantIdCtrl) {
          user.tenantId = result.data.tenantIdCtrl;
        } else if (tid) {
          user.tenantId = tid;
        }

        this.usersManagementService.registerUser(user).subscribe({
          next: (res) => {
            if (res) {
              this.infoBar.showSnackbar(this.variables.successfullyRegisteredUser, ['successful-snackbar'], 4000);
              this.searchFilter(this.searchText);
            }
          },
          error: (error) => {
            console.error('Error:', error.error);
            const message = this.errorMessages[error.error?.message] ?? this.variables?.detectedError;
            this.infoBar.showSnackbar(message, ['error-snackbar'], 4000);
          }
        });
      });
    });
  }

  editUser(element: any) {
    this.runWhenCurrentUserReady(() => {
    const data = {
      title: this.variables.editUser,
      element: {
        id: element.id,
        name: element.name,
        lastName: element.lastName,
        role: element.role,
        email: element.email,
        tenantId: element.tenantId
      },
      canAssignUserTenant: this.canAssignUserCrossTenant()
    };

    let dialogRef = this.dialog.open(ManageUsersDialogComponent, {
      data: data
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {

        let user = {
          name: result.data.nameCtrl,
          lastName: result.data.lastNameCtrl,
          role: result.data.roleCtrl,
          tenantId: result.data.tenantIdCtrl
        };

        const newTenantId = result.data.tenantIdCtrl as string | undefined;
        const tenantChanged = this.canAssignUserCrossTenant()
          && newTenantId && newTenantId !== element.tenantId;

        this.usersManagementService.updateUser(element.id, user).pipe(
          switchMap((updateRes: any) => {
            if (tenantChanged) {
              return this.usersManagementService.assignUserTenant(element.id, { tenantId: newTenantId! }).pipe(
                map((tenantAssignRes: any) => ({ updateRes, tenantAssignRes }))
              );
            }
            return of({ updateRes, tenantAssignRes: null });
          })
        ).subscribe({
          next: ({ updateRes, tenantAssignRes }: { updateRes: any; tenantAssignRes: any }) => {
            this.infoBar.showSnackbar(this.variables.successfullyEditedUser, ['successful-snackbar'], 4000);
            if (tenantChanged) {
              this.infoBar.showSnackbar(this.variables.tenantChangeNotice, ['info-snackbar'], 5000);
            }

            const mustReauthenticate = !!(tenantAssignRes?.mustReauthenticate ?? updateRes?.mustReauthenticate);

            if (mustReauthenticate && element.id === this.id) {
              this.authService.clearToken();
              this.router.navigate(['/login']);
            } else {
              this.searchFilter(this.searchText);
              if (element.id === this.id) {
                this.authService.refreshUserData();
              }
            }
          },
          error: (error) => {
            console.error('Error:', error.error);
            const message = this.errorMessages[error.error?.message] ?? this.variables?.detectedError;
            this.infoBar.showSnackbar(message, ['error-snackbar'], 4000);
          },
          complete: () => {
            console.log('User update and tenant assignment completed');
          }
        });
      }
    });
    });
  }

  rewindPassChange(element: any) {
    let local_data = {
      message: this.variables.sureContinueMessage
    }

    let dialogRef = this.dialog.open(GenericDialogComponent, {
      data: local_data
    });

    dialogRef.afterClosed().subscribe(result => {

      if (result.data) {
        this.usersManagementService.resetUserPass(element.id).subscribe({
          next: (data) => {

            if (data) {
              this.infoBar.showSnackbar(this.variables.successfullyResetUserPass, ['successful-snackbar']);
            }
          },
          error: (error) => {
            console.error('Error forcePassChange user:', error.error);

            const message = this.errorMessages[error.error.message]
            this.infoBar.showSnackbar(message, ['error-snackbar']);


          }
        })
      }
    });
  }


  forcePassChange(element: any) {
    let local_data = {
      message: this.variables.sureContinueMessage
    }

    let dialogRef = this.dialog.open(GenericDialogComponent, {
      data: local_data
    });

    dialogRef.afterClosed().subscribe(result => {

      if (result.data) {
        this.usersManagementService.forceUserPassChange(element.id).subscribe({
          next: (data) => {

            if (data) {
              this.infoBar.showSnackbar(this.variables.successfullyForcedUserPassChange, ['successful-snackbar']);
            }
          },
          error: (error) => {
            console.error('Error forcePassChange user:', error.error);

            const message = this.errorMessages[error.error.message]
            this.infoBar.showSnackbar(message, ['error-snackbar']);


          }
        })
      }
    });
  }


  changeUserActiveStatus(element: any) {
    let local_data = {
      message: this.variables.sureContinueMessage
    }

    let dialogRef = this.dialog.open(GenericDialogComponent, {
      data: local_data
    });

    dialogRef.afterClosed().subscribe(result => {

      if (result.data) {
        this.usersManagementService.changeUserActiveStatus(element.id).subscribe({
          next: (data) => {

            if (data) {
              this.infoBar.showSnackbar(this.variables.successfullyModifiedUserStatus, ['successful-snackbar']);

              this.dataSourceUsers.data.forEach((item: any) => {
                if (item.id == element.id) {
                  item.active = !item.active;
                }
              });
              this.dataSourceUsers.data = [...this.dataSourceUsers.data];
            }
          },
          error: (error) => {
            console.error('Error changing user active status:', error.error);

            const message = this.errorMessages[error.error.message]
            this.infoBar.showSnackbar(message, ['error-snackbar']);

          }
        })
      }
    });
  }
}
